<script setup lang="ts">
import InterviewDialog from '../InterviewDialog.vue';
import InterviewFigure from '../InterviewFigure.vue';
import InterviewImpression from '../InterviewImpression.vue';
import IntervieweeSummary from '../IntervieweeSummary.vue';
import IconPlanner from '/home/runner/_work/shadow/shadow/packages/app/node_modules/.cache/home/runner/_work/shadow/shadow/packages/app/src/components/templates/InterviewTemplate/icon_planner.png/icon_planner.webp';
import ImageModal from './ImageModal.vue';
import Fig4_2 from './fig_4_2.svg';
import { usePinch } from '@vueuse/gesture';
import { mobileMaxWidth } from 'theme/assets/styles/export.module.scss';
import PinchImageBox from '~theme/components/molecules/PinchImageBox.vue';

const props = defineProps<{ interviewee: Interviewee }>();

const PlannerDialog: Component<unknown> = (_: unknown | null, { slots }) =>
  h(
    InterviewDialog,
    { variant: 'planner', avatarUrl: IconPlanner },
    {
      default: (props: { clsNotice: string; clsEm: string }) =>
        slots.default?.(props),
    }
  );

const UserDialog: Component<unknown> = (_: unknown, { slots }) =>
  h(
    InterviewDialog,
    { variant: 'user', avatarUrl: props.interviewee.avatarUrl },
    {
      default: (props: { clsNotice: string; clsEm: string }) =>
        slots.default?.(props),
    }
  );

const imgRef = ref<HTMLElement>();

const { ControlledModal, openModal: openModal_ } = useModalUnnamed(
  undefined,
  ImageModal
);

const { isMobile } = useDeviceType();

const openModal = () => {
  if (isMobile.value) openModal_();
};

const prevent = (e: Event) => {
  if (isMobile.value) e.preventDefault();
};

usePinch(
  ({ offset: [d, _] }) => {
    if (d > 1) openModal();
  },
  {
    domTarget: imgRef,
    eventOptions: {
      passive: false,
    },
  }
);
</script>

<template>
  <div :class="$style.container">
    <ControlledModal>
      <div :class="$style.modal">
        <PinchImageBox :class="$style.modal__img" :src="Fig4_2" />
      </div>
    </ControlledModal>
    <IntervieweeSummary
      :class="$style.container__summary"
      :interviewee="interviewee"
    />

    <PlannerDialog>
      Wさん、こんにちは！<br />
      本日は各種リスクに備える保険のご説明と、もしもの場合にどうやってお子様の教育費を用意していくのかをお話しできればと思っております。よろしくお願いいたします。
    </PlannerDialog>

    <UserDialog> はい、よろしくお願いいたします！ </UserDialog>

    <PlannerDialog>
      最初にお伺いするのですが、Wさんは保険に対するイメージって何かお持ちですか？
    </PlannerDialog>

    <UserDialog>
      イメージ…なんだろうな〜。そうですね、賭けのようなものですかね…？
    </UserDialog>

    <PlannerDialog v-slot="{ clsEm }">
      そうですね、そのようなイメージはみなさん少なくともお持ちです。<br />
      保険とは万が一<span :class="clsEm"
        >ご自身のライフプランが病気や事故で働けなくなったり、死亡してしまったり、想定していないリスクで崩れてしまうのを防ぐためにあらかじめ加入するもの</span
      >となります。ただし、必要以上にお金をかけるものではないですし、場合によっては一生入る必要もありません。必要な期間必要な分だけ備える、一種のサブスクのようなものだと思っていただけるとわかりやすいかなと思います。
    </PlannerDialog>

    <InterviewFigure :class="$style.container__figures" caption="保険の考え方">
      <div :class="$style.figure">
        <picture>
          <source
            srcset="./fig_4_1__sp.svg"
            :media="`(max-width: ${mobileMaxWidth})`"
          />
          <img :class="$style.figure__img" src="./fig_4_1.svg" />
        </picture>
      </div>
    </InterviewFigure>

    <PlannerDialog v-slot="{ clsEm }">
      ではどんなリスクがあるのかと申しますと、<span :class="clsEm"
        >病気やけがで働けなくなるリスク、死亡リスク、がんなどの重たい病気のリスク、病気・けがのリスク</span
      >と大きく4つございます。私たちはすでに公的保険に守られていますので、民間保険で全部に備える必要は必ずしもありません。<span
        :class="clsEm"
        >すでに守られている分をしっかり把握された上で賄いきれない不足部分を民間保険でカバーしていく</span
      >ことで、無駄がなく、かつ過不足もない状態を作ることができます。
    </PlannerDialog>

    <PlannerDialog v-slot="{ clsEm, clsNotice }">
      リスクについてはリスクが高い順にお伝えしていきますね。まずは働けなくなるリスクです。もし、病気やけがで働けなくなった時、会社員・公務員の場合は公的保障として傷病手当金を1年半は給料の約2/3の額がもらえます。その後も働けない場合は障害年金が支払われるのですが、障害年金は大体お給料の半分以下になります。その減ってしまった収入分を補填するのが<span
        :class="clsEm"
        >就業不能保険</span
      >と言われるものです。<span :class="clsNotice"
        >※公的保障の適用には所定の条件を満たす必要があります</span
      >
    </PlannerDialog>

    <UserDialog>
      仮に働けなくなって、収入が減る1/3を他の手段で賄えられればこの保険は必要はないってことですかね。</UserDialog
    >

    <PlannerDialog>
      おっしゃる通りでございます。ただし、絶対に他の手段で賄えるとも限らないのもリスクですね。もちろんご自身のご状況とご意向によるもので、絶対にカバーすることが正解とも限りませんので、一度具体的な保険料感を見られてから保険を使うべきかは決められても良いかもしれませんね。
    </PlannerDialog>

    <UserDialog> わかりました。ありがとうございます！ </UserDialog>

    <PlannerDialog>
      次は死亡リスクについてお話ししますね。こちらも先ほどと同様、Wさんが本来稼ぐはずだったお給料がなくなってしまうリスクとなります。就業不能と違う点としてWさんの生活費が不要になることが挙げられるのですが、それだけですごく支出が減って生活が楽になるわけではないので、ご家族に対する保障としてお考えになっていただければと思います。
    </PlannerDialog>

    <InterviewFigure
      :class="$style.container__figures"
      caption="夫死亡時の遺族年金の目安"
    >
      <div :class="$style.figure">
        <picture>
          <source
            srcset="./fig_4_2__sp.svg"
            :media="`(max-width: ${mobileMaxWidth})`"
          />
          <img
            ref="imgRef"
            :class="$style['figure__img--fullWidth']"
            src="./fig_4_2.svg"
            @click="openModal"
            @gesturestart="prevent"
            @gesturechange="prevent"
            @gesturedragstart="prevent"
          />
        </picture>
      </div>
    </InterviewFigure>

    <PlannerDialog v-slot="{ clsEm }">
      まずは死亡リスクに対して公的保険としては遺族年金というものがございます。こちらは年収と18歳未満のお子様の数によって目安が決まっておりまして、この目安にいくらプラスされればご家族の生活を守れるのか、あとWさんの場合は<span
        :class="clsEm"
        >お子様の教育費が足りるのか</span
      >を考えていけばよろしいと思います。
    </PlannerDialog>

    <PlannerDialog v-slot="{ clsEm }">
      教育費保障の考え方としては、<span :class="clsEm"
        >ご預金で賄う方法と別途専用の保険で賄う方法</span
      >がございます。ご希望はございますか？
    </PlannerDialog>

    <UserDialog>
      うーんそうですね…。今から大学を卒業するための保障が別途あるなら、あるに越したことはないかなと思います。
    </UserDialog>

    <PlannerDialog v-slot="{ clsEm }">
      承知しました、では一旦その方向で置いておきますね。ただこの部分、<span
        :class="clsEm"
        >結局カバーするためにどれだけコストがかかるのか</span
      >という点にもよってくると思うので、そこを踏まえてどこまで保険を使うかは改めて考えていければと思っております。
    </PlannerDialog>

    <UserDialog> はい、大丈夫です。ありがとうございます。 </UserDialog>

    <PlannerDialog v-slot="{ clsEm }">
      では最後、病気・けがのリスクについてお話しさせていただきます。これらのリスクは<span
        :class="clsEm"
        >高額療養費制度</span
      >という制度がポイントになってきます。こちらの制度によってご収入の区分によってひと月の医療費の自己負担の上限が決まっております。
    </PlannerDialog>

    <PlannerDialog v-slot="{ clsNotice }">
      仮に年収370万円〜770万円の場合で、医療費が1ヶ月に100万円（窓口負担30万円）かかった場合でも、この制度によって自己負担額は87,430円となります。これを踏まえていかがでしょうか？<span
        :class="clsNotice"
        >※2024年1月時点</span
      >
    </PlannerDialog>

    <UserDialog>
      月に10万円いかないくらいなら、都度払っちゃったほうが良さそうです。
    </UserDialog>

    <PlannerDialog v-slot="{ clsEm }">
      そうですね、Wさんの場合しっかりと貯金をされているので、必要性はそれほど高くないと思います。関連するのが、がんなどの重たい病気のリスクですね。なぜ重たい病気のリスクで保険が別途あるのかと申しますと、このリスクは治療が<span
        :class="clsEm"
        >長期化しやすい</span
      >ためです。1ヶ月10万円の医療費が1年、3年、5年と継続してかかるリスクをこの保険がカバーしてくれます。
    </PlannerDialog>

    <PlannerDialog v-slot="{ clsEm, clsNotice }">
      こちらについて考える際に考慮してほしい制度として、高額療養制度の他にもう１つ、<span
        :class="clsEm"
        >多数回該当</span
      >という制度がございます。こちら直近の1年以内に3回以上、上限額を超えた場合に4回目からさらに自己負担額を下げてくれる制度です。具体的にどれだけ下がるのかと申しますと、年収370万円〜770万円の場合でしたら、44,400円まで下げることが可能です。<span
        :class="clsNotice"
        >※2024年1月時点</span
      >
    </PlannerDialog>

    <PlannerDialog v-slot="{ clsNotice }">
      この2つの制度を含めると、重たい病気になった場合の1年間の医療費は約67.5万円。他に入院中の食費や個室の場合のベット代を含めますと年間想定費用は約85万円ですね。<span
        :class="clsNotice"
        >※2024年6月時点 ※入院は17日程度（入院費用は約1万円／日）と仮定</span
      >
    </PlannerDialog>

    <UserDialog> この金額を考えるとがん保険は必要な気がしますね…。 </UserDialog>

    <PlannerDialog>
      承知しました！いったん今回の面談を振り返りますね。
    </PlannerDialog>

    <InterviewFigure
      :class="$style.container__figures"
      caption="Wさんの面談振り返り"
    >
      <div :class="$style.figures__col">
        <div :class="$style.figure">
          <div :class="$style.listing">
            <div :class="$style.listing__caption">実践したこと</div>
            <ul :class="$style.listing__listing">
              <li>
                各種リスクに備えるためにどんな公的保険があるのか、<span
                  :class="$style.listing__em"
                  >公的保険でカバーできない部分を補うためにどんな民間保険があるのか</span
                >のご説明
              </li>
              <li>死亡リスクに対しての教育費の備え方</li>
            </ul>
          </div>
        </div>

        <div :class="$style.figure">
          <div :class="$style.listing">
            <div :class="$style.listing__caption">面談を通じて分かったこと</div>
            <ul :class="$style.listing__listing">
              <li>ご自身に必要な保険が何なのか</li>
            </ul>
          </div>
        </div>
      </div>
      <div :class="$style.figure">
        <div :class="$style.listing">
          <div :class="$style.listing__caption">課題・改善・実施したい点</div>
          <ul :class="$style.listing__listing">
            <li>
              必要な保険が見えてきたので、
              <span :class="$style.listing__em"
                >具体的なコストと予算のすり合わせを行い、保障とコストのバランスをとる</span
              >
            </li>
          </ul>
        </div>
      </div>
    </InterviewFigure>

    <PlannerDialog>
      Wさんに必要な保険が見えてきましたので、次回は具体的な保険料を鑑みて保障とコストのバランスをとっていきましょう。
    </PlannerDialog>

    <UserDialog> ぜひともお願いしたいです！ </UserDialog>

    <PlannerDialog>
      ありがとうございます。では引き続きよろしくお願いいたします。
    </PlannerDialog>

    <InterviewImpression
      :class="$style.container__impression"
      :avatar-url="interviewee.avatarUrl"
      notice="※ この記事は、2024年7月にお客様からご相談いただいた内容について、対象のお客様の許可を得て掲載しています ※本レビューの執筆に際し、謝礼をお渡ししました。この対価はレビューの執筆に対する感謝を表すものであり、レビューの内容や意見に影響を与えるものではありません ※内容は、個々の体験や意見に基づいています。お客様の個人的な意見であり、必ずしも全ての方に当てはまるものではありません"
      body="相談内容に合ったアドバイザーの方を選んでくださりとても話しやすかったです。また、私の立場に立ってどんな選択肢があるのかを教えてくださったので納得して保険の契約まで進むことができました。本当にありがとうございました。"
    />
  </div>
</template>

<style module lang="scss">
.modal {
  width: 100%;
  &__img {
    width: 100%;
  }
}

.container {
  display: flex;
  flex-direction: column;
  gap: 24px;

  @include mobile-only {
    gap: 16px;
  }

  &__figures {
    margin: 8px 0;
  }

  &__impression {
    margin-top: 24px;
  }

  &__summary {
    margin-bottom: 24px;
  }
}

.figures {
  &__col {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
}

.figure {
  background-color: $color-surface-primary;
  border-radius: 8px;

  &__img {
    height: 100%;
    max-height: 240px;
    display: block;
    margin: 0 auto;

    @include mobile-only {
      max-height: unset;
      width: 100%;
    }

    &--fullWidth {
      width: 100%;
      height: auto;
      max-height: unset;
    }
  }

  &--withNote {
    position: relative;
  }

  &__note {
    position: absolute;
    bottom: 0;
    right: 24px;
    transform: translateY(100%);
    font-size: 10px;
    padding: 4px 0;
    @include mobile-only {
      position: unset;
      transform: unset;
      padding: unset;
    }
  }
}

.listing {
  line-height: 1.8;
  padding: 16px 24px;
  &__caption {
    font-weight: bold;
  }
  &__listing {
    margin-left: 20px;
    list-style-type: disc;
  }
  &__em {
    font-weight: bold;
    background: linear-gradient(transparent 0% 60%, $color-yellow-300 60%);
    padding-bottom: 2px;
  }
}
</style>
